import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { clearAuth } from '../store/slices/authSlice';
import { showSessionExpiredNotice, setSessionExpiredNoticePending } from '../utils/sessionExpiredNotice';
import { getPreservedBookingState, clearPreservedBookingState, preserveBookingState } from '../utils/sessionManager';
import { startTokenExpirationMonitor, stopTokenExpirationMonitor } from '../utils/tokenExpirationMonitor';

/**
 * Hook to manage session expiration and state restoration
 * @returns {Object} - Session expiration state and handlers
 */
export const useSessionExpiration = () => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Monitor token expiration
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    
    if (token && isAuthenticated) {
      // Start monitoring token expiration
      startTokenExpirationMonitor(token, () => {
        console.log('🔐 Token expired - handling session expiration');
        
        // Check if we're in a booking flow
        let currentPath = window.location.pathname;
        const currentSearch = window.location.search; // Get query parameters
        const bookingPaths = ['/add-passengers', '/payment', '/booking', '/flight-booking-flow'];
        const isBookingFlow = bookingPaths.some(path => currentPath.includes(path));
        
        if (isBookingFlow) {
          // Normalize the path - convert /add-passengers to /flight-booking-flow
          if (currentPath.includes('/add-passengers/')) {
            currentPath = currentPath.replace('/add-passengers/', '/flight-booking-flow/');
            console.log('🔄 Normalized path from /add-passengers to /flight-booking-flow:', currentPath);
          }
          
          // Preserve booking state including query parameters
          const searchParams = new URLSearchParams(currentSearch);
          const bookingState = {};
          
          for (const [key, value] of searchParams.entries()) {
            try {
              bookingState[key] = JSON.parse(value);
            } catch {
              bookingState[key] = value;
            }
          }
          
          // Try to preserve the current step from sessionStorage if available
          const currentStep = sessionStorage.getItem('currentBookingStep');
          if (currentStep) {
            bookingState.currentStep = currentStep;
          }
          
          // IMPORTANT: Preserve passenger form data from sessionStorage
          // Find all passenger data keys for the current user
          const user = JSON.parse(sessionStorage.getItem('user') || '{}');
          const userId = user.id || 'guest';
          
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(`passenger_form_data_${userId}_`)) {
              try {
                const passengerData = sessionStorage.getItem(key);
                if (passengerData) {
                  bookingState.savedPassengerFormData = JSON.parse(passengerData);
                  console.log('💾 Preserved passenger form data:', bookingState.savedPassengerFormData);
                  break; // Only need one match
                }
              } catch (error) {
                console.error('Failed to preserve passenger data:', error);
              }
            }
          }
          
          // Store the full path with query parameters
          const fullPath = currentPath + currentSearch;
          preserveBookingState(bookingState, fullPath);
          
          console.log('💾 Preserved booking state:', { path: fullPath, data: bookingState, step: currentStep });
        }
        
        // Don't clear redirectAfterLogin here - let LoginForm handle it with proper validation
        
        setSessionExpiredNoticePending();
        dispatch(clearAuth());
        
        navigate('/', { replace: true });

        setTimeout(() => {
          showSessionExpiredNotice({ clearSession: false });
        }, 100);
      });
    } else {
      // Stop monitoring if not authenticated
      stopTokenExpirationMonitor();
    }
    
    return () => {
      stopTokenExpirationMonitor();
    };
  }, [isAuthenticated, dispatch, navigate]);

  // Listen for session expiration events (from API interceptor)
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.log('🔐 Session expired event received:', event.detail);
      
      setSessionExpiredNoticePending();
      dispatch(clearAuth());

      navigate('/', { replace: true });

      setTimeout(() => {
        showSessionExpiredNotice({ clearSession: false });
      }, 100);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [dispatch, navigate]);

  // Check for preserved state on mount and after login
  // This is a backup in case LoginForm didn't handle it
  useEffect(() => {
    if (isAuthenticated) {
      const preservedState = getPreservedBookingState();
      
      if (preservedState && preservedState.returnPath) {
        console.log('⚠️ useSessionExpiration: Found preserved state that LoginForm should have handled');
        console.log('📍 Preserved state:', preservedState);
        
        // CRITICAL: Verify the userId matches the current user
        const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const currentUserId = currentUser.id;
        
        if (preservedState.userId && currentUserId && preservedState.userId.toString() === currentUserId.toString()) {
          console.log('✅ User ID matches - restoring booking state (backup)');
          console.log('📍 Return path:', preservedState.returnPath);
          
          const returnUrl = preservedState.returnPath;
          clearPreservedBookingState();
          
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 300);
        } else {
          console.log('⚠️ User ID mismatch in useSessionExpiration - clearing preserved state');
          console.log('Preserved userId:', preservedState.userId, 'Current userId:', currentUserId);
          clearPreservedBookingState();
        }
      }
    }
  }, [isAuthenticated, navigate]);

  const handleLoginSuccess = () => {
    // State restoration will be handled by the useEffect above
    // No need to do anything here
  };

  const handleCloseModal = () => {
    // Clear preserved state if user cancels
    clearPreservedBookingState();
  };

  return {
    showSessionModal,
    handleLoginSuccess,
    handleCloseModal
  };
};

export default useSessionExpiration;
